import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

export type SegmentOption<T extends string> = {
	value: T;
	label: string;
	icon?: ReactNode;
};

type Props<T extends string> = {
	value: T;
	options: SegmentOption<T>[];
	onChange: (value: T) => void;
	disabled?: boolean;
	className?: string;
};

export function SegmentedControl<T extends string>({
	value,
	options,
	onChange,
	disabled,
	className,
}: Props<T>) {
	return (
		<div
			role="group"
			className={cn(
				"inline-flex flex-wrap items-center gap-0.5 rounded-[var(--radius-control)]",
				"bg-zinc-950/[0.05] p-0.5 dark:bg-white/[0.06]",
				className,
			)}
		>
			{options.map((option) => {
				const active = option.value === value;
				return (
					<button
						key={option.value}
						type="button"
						disabled={disabled}
						aria-label={option.label}
						aria-pressed={active}
						onClick={() => onChange(option.value)}
						className={cn(
							"inline-flex h-7 items-center gap-1.5 rounded-[calc(var(--radius-control)-2px)]",
							"px-2.5 text-[13px] font-medium",
							"disabled:pointer-events-none disabled:opacity-40",
							active
								? "bg-surface text-fg shadow-[0_1px_2px_rgb(0_0_0/0.08)] ring-1 ring-[var(--ring-hairline)]"
								: "text-fg-muted hover:text-fg",
						)}
					>
						{option.icon}
						<span className={option.icon ? "hidden sm:inline" : undefined}>
							{option.label}
						</span>
					</button>
				);
			})}
		</div>
	);
}
